import useLocales from "@hooks/useLocales";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Eye, File as FileIcon, Trash } from "lucide-react";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

interface IUploadProps {
  name: string;
  label?: string;
  handleDelete?: () => void;
  isButtonDisabled?: boolean;
  fileName?: string;
  onDrop?: (acceptedFiles: File[]) => void;
  accept?: string[];
  singleUpload?: boolean;
  fileUploadMessage?: string;
  dense?: boolean;
}

export function RHFNewUIUploadSingleFile({
  name,
  label,
  handleDelete,
  isButtonDisabled,
  fileName,
  onDrop,
  accept = ["image/jpeg", "image/png", "application/pdf"],
}: IUploadProps) {
  const { control, setValue } = useFormContext();
  const theme = useTheme();
  const { translate } = useLocales("manual_document_screen");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      if (onDrop) onDrop([selectedFile]);
      setValue(name, selectedFile, { shouldValidate: true });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleView = (fieldValue: any) => {
    if (typeof fieldValue === "string" && fieldValue?.length > 0) {
      window.open(fieldValue, "_blank");
    } else if (fieldValue && fieldValue.preview) {
      window.open(fieldValue.preview, "_blank");
    } else if (fieldValue instanceof File) {
      const objectUrl = URL.createObjectURL(fieldValue);
      window.open(objectUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  };

  const shouldShowViewButton = (fieldValue: any) => {
    return (
      (typeof fieldValue === "string" && fieldValue?.length > 0) ||
      (fieldValue && fieldValue.preview) ||
      fieldValue instanceof File
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const displayFileName =
          fileName ||
          field?.value?.name ||
          (typeof field.value === "string"
            ? translate("supported_document.pdf")
            : "");

        return (
          <Box sx={{ width: "100%" }}>
            {label && (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: theme.palette.action.active,
                  mb: 1,
                }}
              >
                {label}
              </Typography>
            )}

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept={accept.join(",")}
              onChange={handleFileChange}
              disabled={isButtonDisabled}
            />

            {!field.value ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  height: 80,
                  border: "1px solid",
                  borderColor: error ? "error.main" : "#E0E0E0",
                  borderRadius: "8px",
                  bgcolor: theme.palette.background.paper,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FileIcon strokeWidth="1.5px" size="24px" />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    {translate("click_to_upload")}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  border: "1px solid",
                  borderColor: error ? "error.main" : "#E0E0E0",
                  borderRadius: "8px",
                  bgcolor: theme.palette.background.paper,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flex={1}
                  sx={{ minWidth: 0 }}
                >
                  <Tooltip
                    title={displayFileName || translate("no_file_selected")}
                    placement="top-start"
                    arrow
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "default",
                      }}
                      color={theme.palette.text.label}
                    >
                      {displayFileName || translate("no_file_selected")}
                    </Typography>
                  </Tooltip>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {shouldShowViewButton(field.value) && (
                    <IconButton
                      size="small"
                      onClick={() => handleView(field.value)}
                      disabled={isButtonDisabled}
                      sx={{ color: theme.palette.text.label }}
                      title={translate("View")}
                    >
                      <Eye strokeWidth="1.5px" size="18px" />
                    </IconButton>
                  )}

                  {handleDelete && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                      disabled={isButtonDisabled}
                      sx={{
                        color: theme.palette.text.label,
                      }}
                      title={translate("remove")}
                    >
                      <Trash strokeWidth="1.5px" size="18px" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            )}

            {!!error && !field.value && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", mt: 0.5, display: "block" }}
              >
                {error?.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
